import { useState, useEffect, useMemo } from "react";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { evaluatePricing, applyPricingReview, money, quoteDecisionPermission } from "../../models/price-drawer/quotePricingPolicy";
import { injectPartsTemplate } from "../../models/price-drawer/quoteShareModel";
import { buildModifiedQuotePayload } from "../../models/price-drawer/quoteApprovalModel";
import { substituteQRText, loadConditionLabels, conditionLabel, sideLabel } from "../../helpers";
import { useActionSnippet } from "../../../messaging/useActionSnippet";
import { useQuoteApprovalContext } from "./useQuoteApprovalContext";
import { useQuoteApprovalCommands } from "./useQuoteApprovalCommands";

export function useQuoteApproval({ open, onClose, approval, authState, userId, onDone, effectivePriority, notify }) {
  const { can } = useAccess();
  const canDecide = can("approvals.decide") && can(quoteDecisionPermission(approval?.action_key)) && effectivePriority !== null && effectivePriority >= (approval?.required_priority ?? 0);
  const payload = approval?.modified_payload ?? approval?.payload ?? {};
  const belowGpAction = approval?.action_key === "quote_send_below_recommended_gp";
  const [reviewCorrection, setReviewCorrection] = useState(null);
  const [pricingNotice, setPricingNotice] = useState("");
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);

  const [partSp, setPartSp] = useState({});
  const [partVat, setPartVat] = useState({});
  const [partOldExchange, setPartOldExchange] = useState({});
  const [deliveryAmount, setDeliveryAmount] = useState(0);

  const [includedPartIds, setIncludedPartIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("b2c");
  const [messageText, setMessageText] = useState("");
  const [keepAgentText, setKeepAgentText] = useState(true);
  const [textManuallyEdited, setTextManuallyEdited] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const { snippet: actionSnippet, loading: qrLoading, error: bindingError } = useActionSnippet(open, `quote_${activeTab}`);
  // Bumped when the condition code→label map arrives (old payloads store numeric codes)
  const [condVersion, setCondVersion] = useState(0);

  const { costPrices, policyLoading, fullTask, setFullTask, fullTaskLoading, userMap } = useQuoteApprovalContext({ open, payload });

  const isCustomised = !!payload.message_customised;
  const rawType = payload.customer_type ?? null;
  const isB2B = rawType === 0 || rawType === "0";
  const isB2C = rawType === 1 || rawType === "1";

  useEffect(() => {
    if (!open || !payload.parts) return;
    loadConditionLabels().then(() => setCondVersion(v => v + 1));
    const sp = {};
    const vat = {};
    const oldExchange = {};
    const included = new Set();
    (payload.parts ?? []).forEach(p => {
      sp[p.query_part_id] = p.base_sp ?? p.sp;
      // vat_mode is the current field; fall back to the old boolean for
      // approval requests submitted before the mode dropdown existed.
      vat[p.query_part_id] = { pct: p.vat_pct ?? 5, mode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none") };
      oldExchange[p.query_part_id] = !!p.old_exchange;
      if (p.included) included.add(p.query_part_id);
    });
    setPartSp(sp);
    setPartVat(vat);
    setPartOldExchange(oldExchange);
    setIncludedPartIds(included);
    setDeliveryAmount(payload.delivery_amount ?? 0);
    setDeliveryEnabled(payload.delivery_enabled ?? Number(payload.delivery_amount || 0) > 0);
    setReviewCorrection(null); setPricingNotice("");
    setActiveTab(payload.template_type ?? "b2c");
    setMessageText(payload.message_text ?? "");
    setKeepAgentText(true);
    setTextManuallyEdited(false);
    setRejectMode(false);
    setRejectReason("");
    setApproving(false);
    setRejecting(false);
    setIsDirty(false);
  }, [open]); // eslint-disable-line

  const pricingInputs = (payload.parts || []).map(p => ({ ...p,
    base_sp: partSp[p.query_part_id] ?? p.base_sp ?? p.sp,
    included: includedPartIds.has(p.query_part_id) }));
  const pricingKey = JSON.stringify(pricingInputs.map(p => [p.query_part_id, p.cp, p.base_sp, p.included]));
  const originalKey = JSON.stringify((payload.parts || []).map(p => [p.query_part_id, p.cp, p.base_sp ?? p.sp, !!p.included]));
  const localReview = evaluatePricing(pricingInputs, costPrices, fullTask?.task?.timeline);
  const authoritativeReview = reviewCorrection?.key === pricingKey ? reviewCorrection.review
    : pricingKey === originalKey ? payload.pricing_review : null;
  const pricingReview = authoritativeReview ? { ...authoritativeReview, parts: applyPricingReview(localReview.parts, authoritativeReview) } : localReview;
  const pricedById = new Map(pricingReview.parts.map(p => [Number(p.query_part_id), p]));
  const pricingVersionKey = JSON.stringify(pricingReview.parts);
  const needsDedicatedCycle = !belowGpAction && pricingReview.below_recommended_gp;

  const communicationChannel = fullTask?.task?.customer_communication_channel || null;
  const manualChannel = communicationChannel?.key && communicationChannel.key !== "wati";

  const syntheticCard = useMemo(() => ({
    query_id: payload.query_id,
    customer_name: payload.customer_name,
    customer: { customer_name: payload.customer_name },
  }), [payload.query_id, payload.customer_name]);

  const selectedPartsForInjection = useMemo(() =>
    (payload.parts ?? [])
      .filter(p => includedPartIds.has(p.query_part_id))
      .map(p => ({
        part: { part_name: p.part_name, condition_preference: p.condition },
        quote: { warranty_days: p.warranty_days, quoted_price: p.cp },
        sp: pricedById.get(Number(p.query_part_id))?.sp ?? p.sp,
        vatPct: partVat[p.query_part_id]?.pct ?? p.vat_pct ?? 5,
        vatMode: partVat[p.query_part_id]?.mode ?? p.vat_mode ?? (p.vat_enabled ? "incl" : "none"),
        oldExchange: partOldExchange[p.query_part_id] ?? !!p.old_exchange,
      })),
    [payload.parts, includedPartIds, partSp, partVat, partOldExchange, pricingVersionKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deliveryPerPart = selectedPartsForInjection.length > 0
    ? money((deliveryEnabled ? deliveryAmount || 0 : 0) / selectedPartsForInjection.length)
    : 0;

  // Re-inject template text when values change, unless keeping agent's text or admin edited directly
  useEffect(() => {
    if (!open) return;
    if (isCustomised && keepAgentText) return;
    if (textManuallyEdited) return;
    const qr = actionSnippet;
    if (!qr) return;
    const base = substituteQRText(qr.body || "", authState);
    setMessageText(injectPartsTemplate(base, syntheticCard, selectedPartsForInjection, deliveryPerPart, { conditionLabel, sideLabel }));
  }, [open, activeTab, selectedPartsForInjection, actionSnippet, deliveryPerPart, keepAgentText, condVersion]); // eslint-disable-line

  const markDirty = () => setIsDirty(true);

  const togglePart = (qpId) => {
    setIncludedPartIds(prev => {
      const next = new Set(prev);
      if (next.has(qpId)) next.delete(qpId); else next.add(qpId);
      return next;
    });
    markDirty();
  };

  const updateVat = (qpId, key, val) => {
    setPartVat(prev => ({ ...prev, [qpId]: { ...(prev[qpId] ?? { pct: 5, mode: "excl" }), [key]: val } }));
    markDirty();
  };

  const toggleOldExchange = (qpId) => {
    setPartOldExchange(prev => ({ ...prev, [qpId]: !prev[qpId] }));
    markDirty();
  };


  const buildModifiedPayload = () => buildModifiedQuotePayload({ payload, includedPartIds, pricedById, partVat, deliveryPerPart, partOldExchange, deliveryEnabled, deliveryAmount, messageText });
  const { approving, setApproving, rejecting, setRejecting, handleApprove, handleReject, sendOutcome } = useQuoteApprovalCommands({ qrLoading, actionSnippet, bindingError, canDecide, needsDedicatedCycle, policyLoading, selectedPartsForInjection, approval, userId, isDirty, payload, buildModifiedPayload, manualChannel, onDone, onClose, pricingKey, setReviewCorrection, setPricingNotice, setIsDirty, setKeepAgentText, setTextManuallyEdited, setFullTask, rejectReason, notify });
  return { sendOutcome, canDecide, payload, belowGpAction, deliveryEnabled, setDeliveryEnabled, partSp, setPartSp, partVat, partOldExchange, deliveryAmount, setDeliveryAmount, includedPartIds, activeTab, setActiveTab, messageText, setMessageText, keepAgentText, setKeepAgentText, textManuallyEdited, setTextManuallyEdited, rejectMode, setRejectMode, rejectReason, setRejectReason, approving, rejecting, isDirty, actionSnippet, qrLoading, bindingError, fullTask, fullTaskLoading, userMap, isCustomised, isB2B, isB2C, communicationChannel, manualChannel, pricingNotice, needsDedicatedCycle, pricedById, selectedPartsForInjection, deliveryPerPart, markDirty, togglePart, updateVat, toggleOldExchange, handleApprove, handleReject, policyLoading };
}
